import React, { useEffect } from "react"
import { Controller, useForm } from "react-hook-form"
import { Stack, ToggleButton, Typography } from "@mui/material"
import { enqueueSnackbar } from "notistack"
import {
  TextInput,
  ExperienceRatingScale,
  ExperienceRating,
  ToggleButtonGroup
} from "../../../mp-shared-components/generic-shared-components/components"
import { User } from "../../../mp-database"
import { useStyles } from "./styles"

type Props = {
  id: string
  user: User
  promptText?: string
  disabled?: boolean
  onSubmit: (data: FeedbackForm) => void
  onChangeTouched?: (isDirty: boolean) => void
}

export type FeedbackForm = {
  experienceRate: ExperienceRating
  comment: string
  allowToContact: "yes" | "no"
  email: string
}

const defaultValues: FeedbackForm = {
  experienceRate: null,
  comment: "",
  allowToContact: null,
  email: ""
}

const FeedbackTrayForm: React.FC<Props> = ({
  id,
  user,
  promptText,
  disabled = false,
  onSubmit,
  onChangeTouched
}) => {
  const { classes } = useStyles()
  const { control, watch, handleSubmit, formState, setError } =
    useForm<FeedbackForm>({
      defaultValues: {
        ...defaultValues,
        email: user.email
      }
    })

  const selectedRate = watch("experienceRate")
  const allowToContact = watch("allowToContact")

  useEffect(() => {
    onChangeTouched?.(formState.isDirty)
  }, [formState.isDirty, onChangeTouched])

  const onFormSubmit = (data: FeedbackForm) => {
    let hasError = false
    let validEmailRegexp = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/

    if (!data.experienceRate) {
      setError("experienceRate", {
        type: "manual",
        message: "Please select an option to rate your experience."
      })
      hasError = true
    }

    if (!data.comment) {
      setError("comment", {
        type: "manual",
        message: "Please provide a response to continue."
      })
      hasError = true
    }

    if (allowToContact === "yes" && !validEmailRegexp.test(data.email)) {
      setError("email", {
        type: "manual",
        message: "Please enter a valid email address to continue."
      })
      hasError = true
    }

    if (hasError) {
      enqueueSnackbar(
        "Some required fields are missing. Complete them to submit your feedback.",
        { variant: "error" }
      )
      return
    }

    if (!hasError) {
      onSubmit(data)
    }
  }

  return (
    <form id={id} onSubmit={handleSubmit(onFormSubmit)}>
      <Stack gap={6} className={classes.container}>
        {promptText && (
          <Stack gap={3}>
            <Typography variant="body1" id="feedback-text">
              {promptText} *
            </Typography>
            <Controller
              name="experienceRate"
              control={control}
              render={({ field: { onChange } }) => (
                <ExperienceRatingScale
                  selectedRate={selectedRate}
                  disabled={disabled}
                  onChange={onChange}
                  data-automation-id="educator-feedback-rate"
                  aria-labelledby="feedback-text"
                />
              )}
            />
            {formState.errors.experienceRate && (
              <Typography className={classes.errorMessage}>
                {formState.errors.experienceRate.message}
              </Typography>
            )}
          </Stack>
        )}
        <Stack gap={3}>
          <Typography variant="body1">
            What would you like to share with us? *
          </Typography>
          <Controller
            name="comment"
            control={control}
            disabled={disabled}
            render={({ field }) => (
              <TextInput
                multiline
                maxRows={5}
                {...field}
                error={!!formState.errors.comment}
              />
            )}
          />
          {formState.errors.comment && (
            <Typography className={classes.errorMessage}>
              {formState.errors.comment.message}
            </Typography>
          )}
        </Stack>
        {selectedRate &&
          selectedRate !== "smile" &&
          selectedRate !== "grin" && (
            <Stack gap={3}>
              <Typography variant="body1" id="feedback-contact-text">
                We often like to follow up on feedback. Can we contact you?
              </Typography>
              <Controller
                name="allowToContact"
                control={control}
                disabled={disabled}
                render={({ field }) => (
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    aria-labelledby="feedback-contact-text"
                    data-automation-id="educator-feedback-contact-group"
                    {...field}
                  >
                    <ToggleButton
                      value={"yes"}
                      disableRipple
                      data-automation-id="educator-feedback-contact-yes-button"
                    >
                      Yes
                    </ToggleButton>
                    <ToggleButton
                      value={"no"}
                      disableRipple
                      data-automation-id="educator-feedback-contact-no-button"
                    >
                      No
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              />
            </Stack>
          )}
        {allowToContact === "yes" && (
          <Stack gap={3}>
            <Controller
              control={control}
              name="email"
              render={({ field }) => (
                <TextInput
                  placeholder="Email"
                  {...field}
                  error={!!formState.errors.email}
                />
              )}
            />
            {formState.errors.email && (
              <Typography className={classes.errorMessage}>
                {formState.errors.email.message}
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    </form>
  )
}

export default FeedbackTrayForm
